import { useState, useEffect } from 'react'
import {
  MdFlight,
  MdEdit,
  MdArrowBack,
  MdWarning,
  MdCheckCircle,
  MdSchedule,
  MdLocationOn,
  MdBusiness,
  MdEngineering,
  MdAttachMoney,
  MdSecurity,
  MdBuild,
  MdTimeline,
  MdAirplanemodeActive,
  MdSpeed,
  MdHeight,
  MdLocalGasStation,
  MdPeople,
  MdRestaurant,
  MdDescription,
  MdLink,
} from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, LoadingSpinner, Badge, InfoCard } from '../../components/ui'
import { useAircraft } from '../../hooks/useAircraft'
import { useWebsocket } from '../../hooks/useWebsocket'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import { getWebsocketChannel } from '../../utils/websocket'

function AircraftDetailPage() {
  const dispatch = useDispatch()
  const { aircraftId } = useParams()
  const navigate = useNavigate()
  const [aircraft, setAircraft] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const { getAircraftByIdAsync, isGetAircraftByIdLoading } = useAircraft()
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()

  const loadAircraft = async () => {
    try {
      setIsLoading(true)
      const aircraftData = await getAircraftByIdAsync(aircraftId)
      console.log('#aircraftData:', JSON.stringify(aircraftData, null, 2))
      setAircraft(aircraftData)
    } catch (error) {
      console.error('Error loading aircraft:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (aircraftId) {
      loadAircraft()

      const channelName = getWebsocketChannel(aircraftId).aircraftDetails

      if (channelName) {
        subscribeToChannel(channelName)
        dispatch(addSubscription(channelName))
      }

      return () => {
        if (channelName) {
          unsubscribeFromChannel(channelName)
          dispatch(removeSubscription(channelName))
        }
      }
    }
  }, [aircraftId]) // eslint-disable-line react-hooks/exhaustive-deps

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'inactive':
        return 'warning'
      case 'maintenance':
        return 'error'
      case 'retired':
        return 'neutral'
      default:
        return 'neutral'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <MdCheckCircle className="text-success-600 dark:text-success-400" />
      case 'inactive':
        return <MdWarning className="text-warning-600 dark:text-warning-400" />
      case 'maintenance':
        return <MdSchedule className="text-error-600 dark:text-error-400" />
      case 'retired':
        return <MdFlight className="text-neutral-600 dark:text-neutral-400" />
      default:
        return <MdFlight className="text-neutral-600 dark:text-neutral-400" />
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const handleEditAircraft = () => {
    navigate(`/aircraft/${aircraftId}/edit`)
  }

  const handleBackToAircraft = () => {
    navigate(-1)
  }

  if (isLoading || isGetAircraftByIdLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!aircraft) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
            Aircraft Not Found
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            The aircraft you're looking for doesn't exist or has been removed.
          </p>
          <Button variant="primary" onClick={handleBackToAircraft}>
            Back to Aircraft
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="text-left mb-4">
          <Button
            variant="ghost"
            onClick={handleBackToAircraft}
            className="flex items-center space-x-2"
          >
            <MdArrowBack className="text-lg" />
            <span>Back to Aircraft</span>
          </Button>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="text-left">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
                  {aircraft.name}
                </h1>
                <Badge variant={getStatusColor(aircraft.status)}>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(aircraft.status)}
                    <span className="capitalize">{aircraft.status}</span>
                  </div>
                </Badge>
              </div>
              <p className="text-neutral-600 dark:text-neutral-400">
                {aircraft.maker} {aircraft.model} • {aircraft.type} • {aircraft.year}
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            onClick={handleEditAircraft}
            className="flex items-center space-x-2"
          >
            <MdEdit className="text-lg" />
            <span>Edit Aircraft</span>
          </Button>
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <InfoCard title="Aircraft Information" icon={MdFlight}>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Code</span>
              <span className="font-medium text-neutral-900 dark:text-white">{aircraft.code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Registration</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.registrationNumber}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Serial Number</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.serialNumber}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Manufacturer S/N</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.manufacturerSerialNumber}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Line Number</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.lineNumber}
              </span>
            </div>
          </div>
        </InfoCard>
        <InfoCard title="Airline Information" icon={MdBusiness}>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Airline</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.airline?.name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Airline Code</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.airline?.code}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Country</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.airline?.country}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Phone</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.airline?.phone}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Founded</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.airline?.founded ? formatDate(aircraft.airline.founded) : 'N/A'}
              </span>
            </div>
            {aircraft.airline?._id && (
              <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/airlines/${aircraft.airline._id}`)}
                  className="w-full flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  <MdBusiness className="text-sm" />
                  View Airline Details
                </Button>
              </div>
            )}
          </div>
        </InfoCard>
        <InfoCard title="Current Status" icon={MdLocationOn}>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Current Location</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.operationalInfo?.currentLocation || 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Home Base</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.operationalInfo?.homeBase || 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Last Flight</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.operationalInfo?.lastFlight
                  ? formatDateTime(aircraft.operationalInfo.lastFlight)
                  : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Utilization Rate</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.operationalInfo?.utilizationRate
                  ? `${aircraft.operationalInfo.utilizationRate.toFixed(1)}%`
                  : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Daily Utilization</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {aircraft.operationalInfo?.averageDailyUtilization
                  ? `${aircraft.operationalInfo.averageDailyUtilization} hours`
                  : 'N/A'}
              </span>
            </div>
          </div>
        </InfoCard>
      </div>

      {/* Specifications */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdSpeed className="text-primary-600 dark:text-primary-400" />
          Aircraft Specifications
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdHeight className="text-info-600 dark:text-info-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Weight Limits
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <div>
                Takeoff: {aircraft.specifications?.maxTakeoffWeight?.toLocaleString() || 'N/A'} kg
              </div>
              <div>
                Landing: {aircraft.specifications?.maxLandingWeight?.toLocaleString() || 'N/A'} kg
              </div>
            </div>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdLocalGasStation className="text-warning-600 dark:text-warning-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Fuel & Range
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <div>
                Capacity: {aircraft.specifications?.maxFuelCapacity?.toLocaleString() || 'N/A'} L
              </div>
              <div>Range: {aircraft.specifications?.range?.toLocaleString() || 'N/A'} km</div>
            </div>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdSpeed className="text-success-600 dark:text-success-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Performance
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <div>Speed: {aircraft.specifications?.cruisingSpeed || 'N/A'} km/h</div>
              <div>
                Altitude: {aircraft.specifications?.maxAltitude?.toLocaleString() || 'N/A'} ft
              </div>
            </div>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdEngineering className="text-error-600 dark:text-error-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Engines
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <div>Type: {aircraft.specifications?.engines?.type || 'N/A'}</div>
              <div>Count: {aircraft.specifications?.engines?.count || 'N/A'}</div>
              <div>Manufacturer: {aircraft.specifications?.engines?.manufacturer || 'N/A'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdPeople className="text-primary-600 dark:text-primary-400" />
          Cabin Configuration
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
              Seating Layout
            </h3>
            <div className="space-y-3">
              {aircraft.configuration?.cabinClasses?.map((cabinClass, index) => (
                <div key={index} className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {cabinClass.class}
                    </span>
                    <Badge variant="info">{cabinClass.seats} seats</Badge>
                  </div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Layout: {cabinClass.layout}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <MdPeople className="text-warning-600 dark:text-warning-400" />
                <span className="font-medium text-neutral-900 dark:text-white">Cargo Capacity</span>
              </div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {aircraft.configuration?.cargoCapacity?.toLocaleString()} kg
              </p>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
              Galley Locations
            </h3>
            <div className="space-y-2">
              {aircraft.configuration?.galleyLocations?.map((location, index) => (
                <div key={index} className="flex items-center gap-2">
                  <MdRestaurant className="text-primary-600 dark:text-primary-400" />
                  <span className="text-neutral-700 dark:text-neutral-300">{location}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <MdPeople className="text-success-600 dark:text-success-400" />
                <span className="font-medium text-neutral-900 dark:text-white">Total Seats</span>
              </div>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                {aircraft.configuration?.totalSeats}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Maintenance */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdBuild className="text-primary-600 dark:text-primary-400" />
          Maintenance Information
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
              Current Status
            </h3>
            <div className="space-y-4">
              <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MdCheckCircle className="text-success-600 dark:text-success-400" />
                  <span className="font-medium text-neutral-900 dark:text-white">
                    Maintenance Status
                  </span>
                </div>
                <Badge variant="success">{aircraft.maintenance?.maintenanceStatus}</Badge>
              </div>
              <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MdSchedule className="text-info-600 dark:text-info-400" />
                  <span className="font-medium text-neutral-900 dark:text-white">Last Check</span>
                </div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {aircraft.maintenance?.lastCheck
                    ? formatDate(aircraft.maintenance.lastCheck)
                    : 'N/A'}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-500">
                  Type: {aircraft.maintenance?.checkType}
                </p>
              </div>
              <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MdWarning className="text-warning-600 dark:text-warning-400" />
                  <span className="font-medium text-neutral-900 dark:text-white">
                    Next Check Due
                  </span>
                </div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {aircraft.maintenance?.nextCheck
                    ? formatDate(aircraft.maintenance.nextCheck)
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
              Maintenance History
            </h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {aircraft.maintenance?.maintenanceHistory?.map((record, index) => (
                <div key={index} className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-neutral-900 dark:text-white">
                      {record.type} Check
                    </span>
                    <span className="text-xs text-neutral-500 dark:text-neutral-500">
                      {formatDate(record.date)}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
                    {record.description}
                  </p>
                  <div className="flex justify-between items-center text-xs text-neutral-500 dark:text-neutral-500">
                    <span>By: {record.performedBy}</span>
                    <span>Next: {formatDate(record.nextDue)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Airworthiness & Insurance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Airworthiness */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
            <MdSecurity className="text-primary-600 dark:text-primary-400" />
            Airworthiness
          </h2>
          <div className="space-y-4">
            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <MdDescription className="text-info-600 dark:text-info-400" />
                <span className="font-medium text-neutral-900 dark:text-white">Certificate</span>
              </div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
                {aircraft.airworthiness?.certificateNumber}
              </p>
              <div className="text-xs text-neutral-500 dark:text-neutral-500">
                <div>
                  Issued:{' '}
                  {aircraft.airworthiness?.issueDate
                    ? formatDate(aircraft.airworthiness.issueDate)
                    : 'N/A'}
                </div>
                <div>
                  Expires:{' '}
                  {aircraft.airworthiness?.expiryDate
                    ? formatDate(aircraft.airworthiness.expiryDate)
                    : 'N/A'}
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">
                Airworthiness Directives
              </h3>
              <div className="space-y-2">
                {aircraft.airworthiness?.airworthinessDirectives?.map((directive, index) => (
                  <div key={index} className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-neutral-900 dark:text-white">
                        {directive.number}
                      </span>
                      <Badge variant={directive.status === 'Pending' ? 'warning' : 'success'}>
                        {directive.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
                      {directive.description}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-500">
                      Due: {directive.complianceDate ? formatDate(directive.complianceDate) : 'N/A'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Insurance */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
            <MdAttachMoney className="text-primary-600 dark:text-primary-400" />
            Insurance & Financial
          </h2>
          <div className="space-y-4">
            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <MdSecurity className="text-success-600 dark:text-success-400" />
                <span className="font-medium text-neutral-900 dark:text-white">
                  Insurance Policy
                </span>
              </div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
                {aircraft.insurance?.policyNumber}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-500">
                Provider: {aircraft.insurance?.provider}
              </p>
              <p className="text-sm font-medium text-neutral-900 dark:text-white mt-2">
                Coverage: {formatCurrency(aircraft.insurance?.coverageAmount)}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-500">
                Expires:{' '}
                {aircraft.insurance?.expiryDate ? formatDate(aircraft.insurance.expiryDate) : 'N/A'}
              </p>
            </div>
            <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <MdAttachMoney className="text-warning-600 dark:text-warning-400" />
                <span className="font-medium text-neutral-900 dark:text-white">Current Value</span>
              </div>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                {formatCurrency(aircraft.currentValue)}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-500">
                Depreciation Rate: {aircraft.depreciationRate}% per year
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modifications */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdEngineering className="text-primary-600 dark:text-primary-400" />
          Modifications
        </h2>
        <div className="space-y-4">
          {aircraft.modifications?.map((modification, index) => (
            <div key={index} className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <Badge variant="info">{modification.type}</Badge>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {formatDate(modification.date)}
                  </span>
                </div>
                <span className="text-xs text-neutral-500 dark:text-neutral-500">
                  Approved by {modification.approvedBy}
                </span>
              </div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
                {modification.description}
              </p>
              {modification.documentation && (
                <a
                  href={modification.documentation}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                >
                  <MdLink className="text-sm" />
                  View Documentation
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Operational Statistics */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdTimeline className="text-primary-600 dark:text-primary-400" />
          Operational Statistics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MdAirplanemodeActive className="text-success-600 dark:text-success-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Total Flight Hours
              </span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-white">
              {aircraft.operationalInfo?.totalFlightHours?.toLocaleString()}
            </p>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MdSchedule className="text-info-600 dark:text-info-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Total Cycles
              </span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-white">
              {aircraft.operationalInfo?.totalCycles?.toLocaleString()}
            </p>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MdSpeed className="text-warning-600 dark:text-warning-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Utilization Rate
              </span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-white">
              {aircraft.operationalInfo?.utilizationRate?.toFixed(1)}%
            </p>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MdTimeline className="text-error-600 dark:text-error-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Daily Utilization
              </span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-white">
              {aircraft.operationalInfo?.averageDailyUtilization} hrs
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AircraftDetailPage
